import { Box, Button, Typography } from '@mui/material';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import uploadIcon from '../../images/upload.svg';
import { pasteImageFromClipboard } from '../entityImage/useImagePaste';
import '../../css/machines/modifierPhoto.css';

/**
 * Canonical dashed dropzone (folder photo look): drop / browse + clipboard paste.
 * Used for photos, documents, and video files. Do not restyle per module.
 * Video-by-link stays on VideoSourceFields — this widget is upload only.
 */
const VideoFileDropzone = ({
    onFiles,
    onPick,
    primaryText,
    hint,
    accept,
    multiple = false,
    disabled = false,
    showPaste = true,
    onPasteError,
}) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);

    const emit = (fileList) => {
        const list = Array.from(fileList || []).filter(Boolean);
        if (!list.length) {
            return;
        }
        if (onFiles) {
            onFiles(list);
            return;
        }
        if (onPick) {
            onPick(list[0]);
        }
    };

    const setBorder = (event, color) => {
        event.currentTarget.style.borderColor = color;
    };

    const handlePasteClick = async (event) => {
        event.stopPropagation();
        event.preventDefault();
        if (disabled) {
            return;
        }
        const result = await pasteImageFromClipboard();
        if (result.ok) {
            emit([result.file]);
            return;
        }
        onPasteError?.(result.reason);
    };

    return (
        <Box
            className="modifierPhoto_upload_row"
            tabIndex={disabled ? undefined : 0}
            onDragOver={(event) => {
                event.preventDefault();
                if (!disabled) {
                    setBorder(event, '#007bff');
                }
            }}
            onDragLeave={(event) => {
                event.preventDefault();
                setBorder(event, '#C7C7C7');
            }}
            onDrop={(event) => {
                event.preventDefault();
                setBorder(event, '#C7C7C7');
                if (!disabled) {
                    emit(event.dataTransfer?.files);
                }
            }}
            onPaste={(event) => {
                if (disabled) {
                    return;
                }
                const files = event.clipboardData?.files;
                if (files?.length) {
                    event.preventDefault();
                    emit(files);
                }
            }}
        >
            <Box
                className="modifierPhoto_upload_box"
                onClick={() => {
                    if (!disabled) {
                        inputRef.current?.click();
                    }
                }}
                sx={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
                <img src={uploadIcon} alt="" className="modifierPhoto_upload_icons" loading="lazy" decoding="async" />
                <Box>
                    <Typography className="modifierPhoto_uplaod_first_typography">
                        {primaryText || t('Glisser-déposez, collez ou parcourir')}
                    </Typography>
                    {hint ? (
                        <Typography className="modifierPhoto_uplaod_second_typography">{hint}</Typography>
                    ) : null}
                </Box>
            </Box>
            {showPaste ? (
                <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    disabled={disabled}
                    className="modifierPhoto_upload_paste"
                    onClick={handlePasteClick}
                >
                    {t('Coller depuis le presse-papiers')}
                </Button>
            ) : null}
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                disabled={disabled}
                style={{ display: 'none' }}
                onChange={(event) => {
                    emit(event.target.files);
                    event.target.value = '';
                }}
            />
        </Box>
    );
};

export default VideoFileDropzone;
